import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  ATTENDANCE_CHIP_COLORS,
  COVERAGE_CHIP_COLORS,
  OPS_COLORS,
} from '../../constants/operationsDashboardColors';
import { formatCoverageModeLabel, getRowHighlight } from '../../utils/coverageDashboardUtils';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';
import {
  EmployeeDashboardGroup,
  formatAssignmentWindow,
  getEmployeeGroupExpanded,
  groupDashboardRowsByEmployee,
  toggleEmployeeGroupExpanded,
} from '../../utils/employeeDashboardGrouping';
import AttendanceGridCompactView from './AttendanceGridCompactView';

interface AttendanceGridViewProps {
  rows: any[];
  campaignSummary?: any[];
  groupByCampaign?: boolean;
  onToggleGroupByCampaign?: (value: boolean) => void;
  highlightedRowId: string | null;
  rowRefs: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>;
  canManage: boolean;
  onRowAction: (row: any, anchor: HTMLElement) => void;
  onRowClick: (row: any) => void;
  onAssignCoverage?: (row: any) => void;
  onRemoveCoverage?: (row: any) => void;
  onNotRequired?: (row: any) => void;
  onAddNote?: (row: any) => void;
  onViewTimeline?: (row: any) => void;
  density?: DashboardDensity;
}

const AttendanceGridView: React.FC<AttendanceGridViewProps> = ({
  rows,
  campaignSummary = [],
  groupByCampaign = false,
  onToggleGroupByCampaign,
  highlightedRowId,
  rowRefs,
  canManage,
  onRowAction,
  onRowClick,
  onAssignCoverage,
  onRemoveCoverage,
  onNotRequired,
  onAddNote,
  onViewTimeline,
  density = 'standard',
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const compact = isCompact(density);
  const employeeGroups = useMemo(() => groupDashboardRowsByEmployee(rows), [rows]);

  if (compact) {
    return (
      <AttendanceGridCompactView
        rows={rows}
        campaignSummary={campaignSummary}
        groupByCampaign={groupByCampaign}
        onToggleGroupByCampaign={onToggleGroupByCampaign || (() => undefined)}
        highlightedRowId={highlightedRowId}
        rowRefs={rowRefs}
        canManage={canManage}
        onRowClick={onRowClick}
        onAssignCoverage={onAssignCoverage || onRowClick}
        onRemoveCoverage={onRemoveCoverage}
        onNotRequired={onNotRequired || onRowClick}
        onAddNote={onAddNote || onRowClick}
        onViewTimeline={onViewTimeline || onRowClick}
      />
    );
  }

  const cellSx = {
    py: 0.5,
    px: 1,
    fontSize: '0.8rem',
    lineHeight: 1.3,
  };

  const toggle = (id: string, defaultOpen: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => toggleEmployeeGroupExpanded(prev, id, defaultOpen));
  };

  const renderExpanded = (group: EmployeeDashboardGroup) => (
    <Box sx={{ py: 1, px: 1, bgcolor: '#fafafa' }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
        Assignment Timeline
      </Typography>
      {group.assignments.map((assignment) => (
        <Box key={assignment.id} sx={{ display: 'flex', gap: 2, py: 0.35, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ minWidth: 110 }}>{formatAssignmentWindow(assignment)}</Typography>
          <Typography variant="caption" sx={{ minWidth: 140, fontWeight: 600 }}>{assignment.campaignName}</Typography>
          <Typography variant="caption">{assignment.attendanceStatus?.replace(/_/g, ' ')}</Typography>
          <Typography variant="caption">{assignment.coverageStatus?.replace(/_/g, ' ')}</Typography>
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 0.75, mt: 1 }}>
        {[
          ['Clock In', group.clockInTime || '-'],
          ['Clock Out', group.clockOutTime || '-'],
          ['Late Min', group.lateMinutes > 0 ? group.lateMinutes : '-'],
          ['Mode', formatCoverageModeLabel(group.coverageMode)],
        ].map(([label, value]) => (
          <Box key={String(label)}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{label}</Typography>
            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': cellSx }}>
        <TableHead>
          <TableRow>
            <TableCell width={28} sx={{ ...cellSx, py: 0.5 }} />
            {['Employee', 'Campaign', 'Schedule', 'Status', 'Coverage', ''].map((h) => (
              <TableCell key={h || 'actions'} sx={{ fontWeight: 700, bgcolor: 'action.hover', ...cellSx }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {employeeGroups.map((group) => {
            const isOpen = getEmployeeGroupExpanded(expanded, group.id, group.hasMultipleAssignments);
            const attendanceColor = group.attendanceStatus === 'LATE'
              ? ATTENDANCE_CHIP_COLORS.LATE
              : ATTENDANCE_CHIP_COLORS[group.attendanceStatus] || OPS_COLORS.expected;
            const coverageColor = COVERAGE_CHIP_COLORS[group.coverageStatus] || OPS_COLORS.scheduledOff;
            const shiftLabel = group.hasMultipleAssignments
              ? `${group.assignments.length} assignments`
              : formatAssignmentWindow(group.assignments[0]);
            const campaignLabel = group.hasMultipleAssignments
              ? 'Multiple campaigns'
              : group.assignments[0]?.campaignName || '—';

            return (
              <React.Fragment key={group.id}>
                <TableRow
                  hover
                  ref={(el) => { rowRefs.current[group.id] = el; }}
                  sx={{
                    bgcolor: highlightedRowId === group.id || highlightedRowId === group.primaryRow?.id
                      ? '#fff9c4'
                      : getRowHighlight(group.primaryRow),
                    outline: highlightedRowId === group.id ? '2px solid #fbc02d' : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => onRowClick(group.primaryRow)}
                >
                  <TableCell onClick={(e) => toggle(group.id, group.hasMultipleAssignments, e)} sx={{ width: 28, px: 0.25 }}>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {isOpen ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, maxWidth: 140 }}>
                    <Typography variant="inherit" noWrap title={group.agentName}>{group.agentName}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120 }}>
                    <Typography variant="inherit" noWrap title={campaignLabel}>{campaignLabel}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{shiftLabel}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={group.attendanceLabel}
                      sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        bgcolor: attendanceColor,
                        color: '#fff',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={group.coverageLabel} arrow>
                      <Chip
                        size="small"
                        label={group.coverageLabel}
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          bgcolor: coverageColor,
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ width: 32, px: 0.25 }}>
                    {canManage && (
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => onRowAction(group.primaryRow, e.currentTarget)}>
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {renderExpanded(group)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {employeeGroups.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">No records for selected filters.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AttendanceGridView;
